import { cn } from "@/lib/utils"

// Isometric archetype marks (Nucleo isometric set), ported from the
// marketing site's AgentMark and recolored to the app theme: strokes
// follow currentColor, shaded faces use a blue wash.
//   receptionist   — intercom panel (front of house, always answering)
//   lead-responder — flag rising off the platform (outreach on its way)
//   website-chat   — standing monitor (the site, talking)

interface AgentMarkProps {
  kind: "receptionist" | "lead-responder" | "website-chat" | "chat"
  className?: string
}

// The mark each job renders with (per the activation-review direction, the
// receptionist rows use the "chat" intercom mark).
export function markForSlug(slug: string | null): AgentMarkProps["kind"] {
  if (slug === "lead-responder" || slug === "website-chat") return slug
  return "chat"
}

const WASH = "fill-blue-100 dark:fill-blue-950"

export function AgentMark({ kind, className }: AgentMarkProps) {
  if (kind === "chat") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className={cn("size-10", className)}
      >
        <path d="M17.5 22.5V10L20.5 8.5V21L17.5 22.5Z" className={WASH} />
        <path d="M17.5 10L20.5 8.5" stroke="currentColor" strokeWidth="0.75" />
        <path
          d="M3.5 3L17.5 10L17.5 22.5"
          stroke="currentColor"
          strokeWidth="0.75"
        />
        <path
          d="M7 9.25L8.75 10.125L9.625 10.5625"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeLinecap="round"
        />
        <path
          d="M10.5 14.25L8.75 13.375L7 12.5"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeLinecap="round"
        />
        <path
          d="M14 12.75L12.25 11.875"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeLinecap="round"
        />
        <path
          d="M19.9472 21.2764L18.1708 22.1646C17.7485 22.3758 17.2515 22.3758 16.8292 22.1646L7 17.25L5.18045 18.9396C4.5406 19.5337 3.5 19.08 3.5 18.2068V3.61803C3.5 3.23926 3.714 2.893 4.05279 2.72361L5.82918 1.83541C6.25147 1.62426 6.74853 1.62426 7.17082 1.83541L19.9472 8.22361C20.286 8.393 20.5 8.73926 20.5 9.11803V20.382C20.5 20.7608 20.286 21.107 19.9472 21.2764Z"
          stroke="currentColor"
          strokeWidth="0.75"
        />
      </svg>
    )
  }

  if (kind === "receptionist") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className={cn("size-10", className)}
      >
        <path
          d="M19 21L15.7593 22.6011L15.9135 8.74745L18.8843 7.18493L19 21Z"
          className={WASH}
        />
        <path
          d="M16 8.5L18.9291 7.04668"
          stroke="currentColor"
          strokeWidth="0.75"
        />
        <path
          d="M5 3.61803V16.0729C5 16.6411 5.321 17.1605 5.82918 17.4146L15.5528 22.2764C15.8343 22.4172 16.1657 22.4172 16.4472 22.2764L18.4472 21.2763C18.786 21.107 19 20.7607 19 20.3819V7.92705C19 7.35889 18.679 6.8395 18.1708 6.58541L8.44721 1.72361C8.16569 1.58284 7.83431 1.58284 7.55279 1.72361L5.55279 2.72361C5.214 2.893 5 3.23926 5 3.61803Z"
          stroke="currentColor"
          strokeWidth="0.75"
        />
        <path d="M5 3L16 8.5V22.5" stroke="currentColor" strokeWidth="0.75" />
        <path
          d="M7 10.882V6.80902C7 6.43733 7.39116 6.19558 7.72361 6.3618L13.4472 9.22361C13.786 9.393 14 9.73926 14 10.118V14.191C14 14.5627 13.6088 14.8044 13.2764 14.6382L7.55279 11.7764C7.214 11.607 7 11.2607 7 10.882Z"
          className={WASH}
        />
        <path
          d="M6.82689 14.2052C6.85043 13.6611 7.25449 13.4308 7.72938 13.6909C8.20427 13.9509 8.57016 14.6028 8.54662 15.147C8.52308 15.6911 8.11902 15.9214 7.64412 15.6613C7.16923 15.4012 6.80334 14.7493 6.82689 14.2052Z"
          fill="currentColor"
        />
        <path
          d="M9.47899 15.527C9.50253 14.9828 9.90659 14.7526 10.3815 15.0126C10.8564 15.2727 11.2223 15.9246 11.1987 16.4687C11.1752 17.0129 10.7711 17.2431 10.2962 16.9831C9.82133 16.723 9.45544 16.0711 9.47899 15.527Z"
          fill="currentColor"
        />
        <path
          d="M12.1311 16.8436C12.1546 16.2995 12.5587 16.0692 13.0336 16.3293C13.5085 16.5893 13.8744 17.2413 13.8508 17.7854C13.8273 18.3295 13.4232 18.5598 12.9483 18.2997C12.4734 18.0397 12.1075 17.3877 12.1311 16.8436Z"
          fill="currentColor"
        />
      </svg>
    )
  }

  if (kind === "lead-responder") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className={cn("size-10", className)}
      >
        <path d="M23 4.5L21 5.5V11.5L23 10.5V4.5Z" className={WASH} />
        <path d="M21 19.5L13 23.5V20.5L21 16.5V19.5Z" className={WASH} />
        <path
          d="M13 20.2938V23.2938"
          stroke="currentColor"
          strokeWidth="0.75"
        />
        <path
          d="M13 7.49993V2.11803C13 1.73926 13.214 1.393 13.5528 1.22361L14.5528 0.723607C14.8343 0.582843 15.1657 0.582843 15.4472 0.723607L22.4472 4.22361C22.786 4.393 23 4.73926 23 5.11803V9.88199C23 10.2608 22.786 10.607 22.4472 10.7764L21.6708 11.1646C21.2485 11.3758 20.7515 11.3758 20.3292 11.1646L16 9.00002L14.2301 10.4748C13.7416 10.8819 13 10.5345 13 9.89867V7.49993Z"
          stroke="currentColor"
          strokeWidth="0.75"
        />
        <path
          d="M20.9999 11.5L21 5.5L13 1.5"
          stroke="currentColor"
          strokeWidth="0.75"
        />
        <path d="M21 5.5L23 4.5" stroke="currentColor" strokeWidth="0.75" />
        <path
          d="M1 14.5L13 20.5L21 16.5"
          stroke="currentColor"
          strokeWidth="0.75"
        />
        <path
          d="M8.5 12.75L6 14"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeLinecap="round"
        />
        <path
          d="M8.32918 10.8354L1.55279 14.2236C1.214 14.393 1 14.7393 1 15.118V16.882C1 17.2607 1.214 17.607 1.55279 17.7764L12.3292 23.1646C12.7515 23.3757 13.2485 23.3757 13.6708 23.1646L20.4472 19.7764C20.786 19.607 21 19.2607 21 18.882V17.118C21 16.7393 20.786 16.393 20.4472 16.2236L9.67082 10.8354C9.24853 10.6243 8.75147 10.6243 8.32918 10.8354Z"
          stroke="currentColor"
          strokeWidth="0.75"
        />
      </svg>
    )
  }

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn("size-10", className)}
    >
      <path d="M15 21.5L9 18.5V16.5L15 19.5V21.5Z" className={WASH} />
      <path
        d="M17.5 18.25L3.5 11.25"
        stroke="currentColor"
        strokeWidth="0.75"
      />
      <path d="M17.5 21V9.5L20.5 8V19.5L17.5 21Z" className={WASH} />
      <path d="M17.5 9.5L20.5 8" stroke="currentColor" strokeWidth="0.75" />
      <path
        d="M3.5 2.5L17.5 9.5L17.5 21"
        stroke="currentColor"
        strokeWidth="0.75"
      />
      <path
        d="M9 16.6824V17.882C9 18.2608 8.78601 18.607 8.44723 18.7764L6.8944 19.5529C6.52588 19.7372 6.52588 20.263 6.8944 20.4473L11.5528 22.7765C11.8343 22.9173 12.1657 22.9173 12.4472 22.7765L14.4474 21.7764C14.7862 21.607 15.0002 21.2607 15.0002 20.8819V19.7189"
        stroke="currentColor"
        strokeWidth="0.75"
      />
      <path
        d="M3.5 13.382V3.11803C3.5 2.73926 3.714 2.393 4.05279 2.22361L5.82918 1.33541C6.25147 1.12426 6.74853 1.12426 7.17082 1.33541L19.9472 7.72361C20.286 7.893 20.5 8.23926 20.5 8.61803V18.882C20.5 19.2608 20.286 19.607 19.9472 19.7764L18.1708 20.6646C17.7485 20.8758 17.2515 20.8758 16.8292 20.6646L4.05279 14.2764C3.714 14.107 3.5 13.7608 3.5 13.382Z"
        stroke="currentColor"
        strokeWidth="0.75"
      />
    </svg>
  )
}
